// Left chat panel — conversation with the agent drives the design.

import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import { fetchPlan, uploadImage, type CritiqueIssue } from '../api';
import { ReasoningText } from './loading';
import { ChevronLeftIcon, PlusIcon, XIcon, ArrowUpIcon, StopIcon } from './icons';
import type { ChatMsg, ChatProcessStep, TurnProgress } from '../types';

export interface ChatPanelProps {
  chat: ChatMsg[];
  busy: boolean;
  onSend: (text: string, images: string[]) => void;
  onRetry: (text: string) => void;
  /** Panel collapse/expand */
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** SPEC-392 段B / SPEC-400: a visual review loop is running → the send button doubles as stop */
  loopActive?: boolean;
  onStop?: () => void;
  /** SPEC-414 T-b: live async-turn progress from the poll channel — minimal honest version: rounds-done counter */
  progress?: TurnProgress | null;
  /** SPEC-413 M1b: plan-card 「重试」 — App resends the last user message with replan:'mutate' (§6 决策 f/h). */
  onPlanRetry?: () => void;
  /** SPEC-426 M2 A.3 (修复轮): done 卡片「继续修正」 — App 以残留清单发起新 refine 轮并重入审查循环.
   * SPEC-427 A: frozen=true 的清单已被熔断——App 侧直接拒绝并复述诊断（按钮同步禁用，双重保险）. */
  onContinueRefine?: (residual: CritiqueIssue[], frozen?: boolean) => void;
}

const SUGGESTION_KEYS = ['chat.suggestion.1', 'chat.suggestion.2', 'chat.suggestion.3'];
const MAX_INPUT_PX = 160; // auto-grow cap; beyond this the textarea scrolls
const ONE_LINE_PX = 35; // one line exactly: 21px line-height + 12px padding + 2px borders (SPEC-373/389)
// Composer attachments (SPEC-384): upload-on-attach via the existing /api/upload; the returned
// same-origin /api/img/ URL is what rides into /api/chat (the worker accepts nothing else).
const MAX_ATTACH = 4; // keep in sync with the /api/chat cap
const MAX_FILE_BYTES = 8 * 1024 * 1024; // /api/upload limit

// SPEC-392 段C: collapsible review-loop process log — consumes ChatMsg.process recorded by
// segment B (this segment produces no data). Collapsed by default; muted colors, 1px hairlines,
// same family as the chat bubbles. Screenshot URLs are /api/img/ with a 3-day TTL: on load
// failure we render a placeholder block (never a broken image).
function ProcessThumb({ url }: { url: string }) {
  const t = useT();
  const [failed, setFailed] = useState(false);
  if (failed) return <span className="process-thumb-expired">{t('chat.processImgExpired')}</span>;
  return <img className="process-thumb" src={url} alt="" loading="lazy" onError={() => setFailed(true)} />;
}

// SPEC-413 M1b — 方案卡片（§6 决策 a/f/h）：默认收起 digest，展开懒加载完整 DesignPlan（GET /api/plan）；
// 三选项非阻塞：确定=本地确认态 / 重试=replan:'mutate' 强制变异重规划 / 提修改=作为普通消息发送（修订 diff 子图重执行=M2）。
// 飞行中中断走既有 stop 键；busy 时重试禁用（in-flight mutation = M2 范围）。
function PlanStep({ step, busy, onSend, onPlanRetry }: { step: ChatProcessStep; busy?: boolean; onSend?: (text: string) => void; onPlanRetry?: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState<any | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [inputOpen, setInputOpen] = useState(false);
  const [draft, setDraft] = useState('');
  useEffect(() => {
    if (!open || plan || failed || !step.planKey) return;
    let alive = true;
    void fetchPlan(step.planKey).then(p => { if (alive) (p ? setPlan(p) : setFailed(true)); });
    return () => { alive = false; };
  }, [open, plan, failed, step.planKey]);
  const zones: any[] = Array.isArray(plan?.layout?.zones) ? plan.layout.zones : [];
  return (
    <div className="plan-card">
      <button type="button" className="process-summary" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <span className="process-caret" aria-hidden="true">❯</span>
        {t('chat.processStep.plan')}{step.planDigest ? ` · ${step.planDigest}` : ''}{confirmed ? ` · ${t('chat.plan.confirmed')}` : ''}
      </button>
      {open && (
        <div>
          {!plan && !failed && <div className="plan-section-title">…</div>}
          {failed && <div className="plan-section-title">{t('chat.plan.loadFailed')}</div>}
          {plan && (<>
            <div className="plan-section"><div className="plan-section-title">{t('chat.plan.intent')}</div><div>{plan.intent?.read}</div></div>
            {zones.length > 0 && (
              <div className="plan-section">
                <div className="plan-section-title">{t('chat.plan.zones')}</div>
                <div className="plan-zones">
                  {zones.map(z => (
                    <div key={z.id} className="plan-zone" title={z.role} style={{ left: `${z.region[0] * 100}%`, top: `${z.region[1] * 100}%`, width: `${z.region[2] * 100}%`, height: `${z.region[3] * 100}%` }}>{z.id}</div>
                  ))}
                </div>
              </div>
            )}
            {Array.isArray(plan.objects) && plan.objects.length > 0 && (
              <div className="plan-section">
                <div className="plan-section-title">{t('chat.plan.objects')}</div>
                <ul className="plan-objects">{plan.objects.map((o: any) => <li key={o.id}>{o.id} [{o.kind}] {o.role}{o.zone ? ` @${o.zone}` : ''}{o.content ? `：${o.content}` : ''}</li>)}</ul>
              </div>
            )}
            {Array.isArray(plan.delivery_bar) && plan.delivery_bar.length > 0 && (
              <div className="plan-section">
                <div className="plan-section-title">{t('chat.plan.bar')}</div>
                <ul className="plan-bar">{plan.delivery_bar.map((b: string, i: number) => <li key={i}>☐ {b}</li>)}</ul>
              </div>
            )}
            {Array.isArray(plan.known_limits) && plan.known_limits.length > 0 && (
              <div className="plan-section"><div className="plan-section-title">{t('chat.plan.limits')}</div><div className="plan-limits">⚠ {plan.known_limits.join('；')}</div></div>
            )}
          </>)}
          <div className="plan-actions">
            <button type="button" className="plan-btn primary" disabled={confirmed} onClick={() => setConfirmed(true)}>{confirmed ? t('chat.plan.confirmed') : t('chat.plan.confirm')}</button>
            <button type="button" className="plan-btn" disabled={busy || !onPlanRetry} onClick={() => onPlanRetry?.()}>{t('chat.plan.retry')}</button>
            <button type="button" className="plan-btn" onClick={() => setInputOpen(v => !v)}>{t('chat.plan.input')}</button>
            {inputOpen && (
              <input
                className="plan-input"
                placeholder={t('chat.plan.inputPlaceholder')}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && draft.trim() && onSend) { onSend(draft.trim()); setDraft(''); setInputOpen(false); } }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ProcessDetails({ steps }: { steps: ChatProcessStep[] }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const totalMs = steps.reduce((a, s) => a + (s.ms ?? 0), 0);
  return (
    <div className="chat-process">
      <button
        type="button"
        className="process-summary"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <span className="process-caret" aria-hidden="true">❯</span>
        {t('chat.processTitle', { n: steps.length, s: (totalMs / 1000).toFixed(1) })}
      </button>
      {open && (
        <div className="process-steps">
          {steps.map((s, i) => (
            <div key={i} className="process-step">
              {/* SPEC-412: phase labels carry round scope — stage completion must never read as project completion */}
              <span className="process-phase">{t(`chat.processStep.${s.phase}`)} · {t('chat.processRound', { n: steps.slice(0, i + 1).filter(p => p.phase === s.phase).length })}</span>
              {s.ms != null && <span className="process-ms">{(s.ms / 1000).toFixed(1)}s</span>}
              {s.verdict && (
                <span className={`process-verdict${s.verdict.pass ? '' : ' fail'}`}>
                  {t(s.verdict.pass ? 'chat.processPass' : 'chat.processFail')}
                  {' · '}
                  {t('chat.processHardSoft', { h: s.verdict.hard, s: s.verdict.soft })}
                </span>
              )}
              {/* SPEC-426 M2 A.3: regression marker — 上轮点名要修的问题本轮重现/hard 总数上升 */}
              {s.regression && <span className="process-regression">{t('chat.processRegression')}</span>}
              {s.metrics && (
                <span className="process-note">
                  {t('chat.processDiffMetrics', {
                    d: s.metrics.paletteDist == null ? '—' : s.metrics.paletteDist.toFixed(1),
                    r: Math.round(s.metrics.textRate * 100),
                  })}
                  {s.metrics.missing.length > 0 && ` · ${s.metrics.missing.join('、')}`}
                </span>
              )}
              {s.note && <span className="process-note">{s.note}</span>}
              {s.phase === 'screenshot' && s.screenshotUrl && <ProcessThumb url={s.screenshotUrl} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ChatPanel({ chat, busy, onSend, onRetry, collapsed, onToggleCollapse, loopActive, onStop, progress, onPlanRetry, onContinueRefine }: ChatPanelProps) {
  const t = useT();
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<{ url: string; uploading: boolean; error?: boolean }[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const attachFile = (file: File) => {
    if (!/^image\/(png|jpeg)$/.test(file.type) || file.size > MAX_FILE_BYTES) return;
    const localUrl = URL.createObjectURL(file);
    setAttachments(a => (a.length >= MAX_ATTACH ? a : [...a, { url: localUrl, uploading: true }]));
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const url = await uploadImage(String(reader.result));
        URL.revokeObjectURL(localUrl);
        setAttachments(a => a.map(x => (x.url === localUrl ? { url, uploading: false } : x)));
      } catch {
        setAttachments(a => a.map(x => (x.url === localUrl ? { ...x, uploading: false, error: true } : x)));
      }
    };
    reader.readAsDataURL(file);
  };
  const removeAttach = (url: string) => setAttachments(a => a.filter(x => x.url !== url));

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [chat, busy]);

  // Auto-grow input (.impeccable.md §7): one row at rest, grows with content up to MAX_INPUT_PX then scrolls.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    // border-box sizing: scrollHeight excludes borders, so add them back — otherwise the box comes out
    // 2px short of one line and overflow-y shows a permanent scrollbar thumb (SPEC-373, SPEC-370 regression).
    const cs = getComputedStyle(el);
    const borders = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    if (el.value === '') {
      // SPEC-389: empty → pin exactly one line. scrollHeight with an empty value can report two lines
      // (placeholder / font-metric quirks), which inflated the box to 56px — 21px taller than the 35px
      // buttons it sits between (Dale 15:28 screenshot, pixel-measured).
      // SPEC-390: overflow hidden while pinned — any metric drift (line box >21px) must never surface a
      // scrollbar in the single-line resting state.
      el.style.height = `${ONE_LINE_PX}px`;
      el.style.overflowY = 'hidden';
      return;
    }
    el.style.height = 'auto';
    const needed = el.scrollHeight + borders;
    el.style.height = `${Math.min(needed, MAX_INPUT_PX)}px`;
    // SPEC-390: the box grows with content up to the cap, so no scrollbar is needed below it —
    // overflow-y only kicks in past MAX_INPUT_PX. Kills the ghost scrollbar from metric drift.
    el.style.overflowY = needed > MAX_INPUT_PX ? 'auto' : 'hidden';
  }, [input, collapsed]);

  const uploading = attachments.some(a => a.uploading);
  const readyImgs = attachments.filter(a => !a.uploading && !a.error).map(a => a.url);
  const canSend = !busy && !uploading && !attachments.some(a => a.error) && (input.trim().length > 0 || readyImgs.length > 0);

  const submit = () => {
    if (!canSend) return;
    const text = input.trim();
    const imgs = readyImgs;
    setInput('');
    setAttachments([]);
    onSend(text, imgs);
  };

  if (collapsed) {
    // SPEC-380: the expand button moved into the canvas topbar (pushes the page tabs aside) — the collapsed
    // panel itself is an empty zero-width strip.
    return <aside className="chat-panel collapsed" />;
  }

  return (
    <aside className="chat-panel">
      <div className="panel-header">
        {t('chat.title')}
        <button
          className="panel-collapse-btn"
          onClick={onToggleCollapse}
          title={t('panel.collapse.chat')}
          aria-label={t('panel.collapse.chat')}
        >
          <ChevronLeftIcon />
        </button>
      </div>

      <div ref={listRef} className="chat-list">
        {chat.length === 0 && (
          <div className="chat-empty">
            <p className="hint">{t('chat.empty')}</p>
            {SUGGESTION_KEYS.map(k => (
              <button key={k} className="suggestion" onClick={() => !busy && onSend(t(k), [])}>{t(k)}</button>
            ))}
          </div>
        )}
        {chat.map((m, i) => (
          <div key={i} className={`chat-msg ${m.role}${m.error ? ' error' : ''}`}>
            <div className="bubble">
              {m.images?.map(u => <img key={u} className="chat-msg-img" src={u} alt="" />)}
              {m.text}
              {/* SPEC-392 段C: process log only exists on the loop's done bubble — messages without
                  process render exactly as before (no DOM, no style change). */}
              {m.process?.some(s => s.phase === 'plan') && m.process.filter(s => s.phase === 'plan').map((s, i) => (
                <PlanStep key={`plan-${i}`} step={s} busy={busy} onSend={(txt) => onSend(txt, [])} onPlanRetry={onPlanRetry} />
              ))}
              {m.process && m.process.some(s => s.phase !== 'plan') && <ProcessDetails steps={m.process.filter(s => s.phase !== 'plan')} />}
              {/* SPEC-426 M2 A.3 (修复轮): 预算耗尽仍有 hard → 残留问题清单 + 一键继续修正。
                  旧消息无 residual 字段 → 零 DOM（零迁移，渲染不变）. */}
              {m.residual && m.residual.length > 0 && (
                <div className="residual-block">
                  {/* SPEC-427 A: frozen 清单换诊断标题 + 建议动作；按钮禁用（非隐藏）. */}
                  <div className="residual-title">{t(m.frozen ? 'chat.frozenTitle' : 'chat.residualTitle')}</div>
                  <ul className="residual-list">
                    {m.residual.map((r, ri) => (
                      <li key={ri}>{r.desc}{r.region ? <span className="residual-region">（位置：{r.region}）</span> : null}</li>
                    ))}
                  </ul>
                  {m.frozen && <div className="residual-advice">{t('chat.frozenAdvice')}</div>}
                  {onContinueRefine && (
                    <button className="retry-link" disabled={busy || !!m.frozen} onClick={() => onContinueRefine(m.residual!, m.frozen)}>{t('chat.continueRefine')}</button>
                  )}
                </div>
              )}
              {/* SPEC-426 M2 A.4 (修复轮): 维度评分徽标（0-2）——旧消息/旧响应无 scores → 整体
                  隐藏（提示 3 回退：不 crash 不空白，残留/issues 清单不受影响）. */}
              {m.scores && Object.keys(m.scores).length > 0 && (
                <div className="scores-row">
                  <span className="scores-title">{t('chat.scoresTitle')}</span>
                  {Object.entries(m.scores).map(([k, v]) => (
                    <span key={k} className={`score-badge${v === 0 ? ' low' : ''}`}>{k} {v}/2</span>
                  ))}
                </div>
              )}
              {m.error && m.retryText && (
                <button className="retry-link" disabled={busy} onClick={() => onRetry(m.retryText!)}>{t('chat.retry')}</button>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="chat-msg assistant">
            <div className="bubble thinking">
              <ReasoningText phrases={[t('chat.phase.0'), t('chat.phase.1'), t('chat.phase.2'), t('chat.phase.3')]} />
              {progress && progress.rounds_done > 0 && (
                <span className="turn-rounds">{t('chat.turnRound', { n: progress.rounds_done })}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="chat-attach-row">
          {attachments.map(a => (
            <span
              key={a.url}
              className={`chat-attach-thumb${a.uploading ? ' uploading' : ''}${a.error ? ' error' : ''}`}
              title={a.error ? t('chat.attachFail') : undefined}
              onClick={a.error ? () => removeAttach(a.url) : undefined}
            >
              <img src={a.url} alt="" />
              <button
                className="chat-attach-x"
                onClick={e => { e.stopPropagation(); removeAttach(a.url); }}
                aria-label="remove"
              ><XIcon /></button>
            </span>
          ))}
        </div>
      )}

      <div className="chat-input-row">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg"
          hidden
          onChange={e => { const f = e.target.files?.[0]; if (f) attachFile(f); e.target.value = ''; }}
        />
        <button
          className="ghost-btn attach-btn"
          title={t('chat.attach')}
          disabled={busy || uploading || attachments.length >= MAX_ATTACH}
          onClick={() => fileRef.current?.click()}
        ><PlusIcon /></button>
        <textarea
          ref={taRef}
          value={input}
          placeholder={busy ? t('chat.placeholderBusy') : t('chat.placeholder')}
          disabled={busy}
          rows={1}
          onChange={e => setInput(e.target.value)}
          onPaste={e => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (let i = 0; i < items.length; i++) {
              const it = items[i];
              if (it.type.startsWith('image/')) {
                const f = it.getAsFile();
                if (f) { e.preventDefault(); attachFile(f); return; }
              }
            }
          }}
          /* SPEC-393 (Dale 08-29): send only via the send button — Enter keeps its native behavior
             (insert newline; auto-grow supports multiline up to MAX_INPUT_PX). Removes the accidental
             Enter-to-send, and as a side effect no longer interrupts IME composition (the old handler
             never checked isComposing). RegionComposer keeps its modal Enter-confirm — different flow. */
        />
        {/* SPEC-400 (Dale 08-29): no separate stop button — the send button doubles as stop while the
            review loop runs. Standard +/input/send layout stays intact. */}
        <button
          className="primary-btn send-btn"
          onClick={loopActive ? onStop : submit}
          disabled={!loopActive && !canSend}
          title={loopActive ? t('chat.stopLoop') : t('chat.send')}
          aria-label={loopActive ? t('chat.stopLoop') : t('chat.send')}
        >{loopActive ? <StopIcon /> : <ArrowUpIcon size={14} />}</button>
      </div>
    </aside>
  );
}
